import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Star } from "lucide-react";
import SubmitReview from "./SubmitReview";

interface PastBookingCardProps {
  booking: any;
  start: string;
  end: string;
  amount: string;
  title: string;
  currentUserId?: string;
}

export function PastBookingCard({ 
  booking, 
  start, 
  end, 
  amount, 
  title,
  currentUserId 
}: PastBookingCardProps) {
  const [showReviewForm, setShowReviewForm] = useState(false);

  // Check if user has already reviewed this booking
  const { data: existingReview } = useQuery({
    queryKey: ["review", booking.id, currentUserId],
    enabled: !!booking.id && !!currentUserId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("*")
        .eq("booking_id", booking.id)
        .eq("reviewer_id", currentUserId)
        .eq("review_type", "guest_to_host")
        .single();

      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
  });

  // Get host name from listing owner
  const { data: hostData } = useQuery({
    queryKey: ["host", booking.listings?.owner_id],
    enabled: !!booking.listings?.owner_id,
    queryFn: async () => {
      const { data } = await supabase
        .from("users")
        .select("name")
        .eq("id", booking.listings.owner_id)
        .single();
      return data;
    },
  });

  const canReview = 
    booking.status === "confirmed" || 
    booking.status === "completed";

  return (
    <Card>
      <CardContent className="p-4 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1 space-y-1">
            <div className="font-semibold">{title}</div>
            <div className="text-sm text-muted-foreground">
              {start} — {end}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant={
              booking.status === "confirmed" || booking.status === "completed"
                ? "default"
                : "destructive"
            }>
              {booking.status}
            </Badge>
            <div className="font-semibold whitespace-nowrap">{amount}</div>
          </div>
        </div>

        {/* Review Section */}
        {canReview && (
          <div className="border-t pt-4">
            {existingReview ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-4 w-4 ${
                          star <= existingReview.rating
                            ? "fill-warning text-warning"
                            : "text-muted-foreground"
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Din anmeldelse
                  </span>
                </div>
                {existingReview.text && (
                  <p className="text-sm text-muted-foreground">
                    {existingReview.text}
                  </p>
                )}
              </div>
            ) : showReviewForm ? (
              <SubmitReview
                bookingId={booking.id}
                listingTitle={title}
                reviewType="guest_to_host"
                targetUserName={hostData?.name || "værten"}
                onSubmitted={() => setShowReviewForm(false)}
              />
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowReviewForm(true)}
                className="w-full sm:w-auto"
              >
                <Star className="h-4 w-4 mr-2" />
                Anmeld {hostData?.name || "værten"}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
